/* eslint-disable max-lines, unicorn/filename-case -- TODO fix later */
import { clientSDK, queryClient } from "@istari-frontend/api-client";
import { formatDate } from "@istari-frontend/shared";
import {
  AccessRelationship,
  AccessResourceType,
  AssignInfosecLevel,
  InfosecLevel,
  NewSnapshot,
  NewSystem,
  NewSystemConfiguration,
  NewTrackedFile,
  OptionalTotalPageSystem,
  PageSnapshotItem,
  ResourceSearchItem,
  Snapshot,
  SnapshotItem,
  SnapshotRevisionSearchItem,
  SnapshotTag,
  SystemConfiguration,
  UpdateSystem,
} from "@istari/istari-client";
import {
  QueryKey,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { toast } from "sonner";

import { DEFAULT_CACHE_TIME } from "~/constants";
import {
  AccessActionType,
  HttpStatusCodeType,
  ResourceType,
  SystemFileRevisionSpecifierType,
  SystemResourceType,
  UserGrantType,
} from "~/enums";
import { systemAccessQueryKey } from "~/systems/data-source/system-access-query-key";
import { downloadFilesInZip } from "~/systems/data-source/zip-download";
import type { SystemAccessRelationshipPayload } from "~/systems/model/system-access-member";
import { getFileNameAndExtension, stripExtensionFromDisplayName } from "~/utils/file";

import { ConfigurationTrackedFile } from "./configurations";
import { ListOptions, SystemListOptions } from "./model/modelQueryKeys";
import { snapshotRevisionsQueryOptions } from "./snapshot-files";
import { lookupOrFetchUser, ModelProperties } from "./useModels";

type SnapshotLoadError =
  | { type: "no_access"; revisionId: string }
  | { type: "not_found"; revisionId: string }
  | { type: "unknown"; message?: string };

export const systemsQueryKeys = {
  all: ["systems"],
  item: (id: string, snapshotId?: string) =>
    snapshotId ? [...systemsQueryKeys.all, id, "snapshot", snapshotId] : [...systemsQueryKeys.all, id],
  list: (filter: ListOptions) => [...systemsQueryKeys.all, "list", filter],
  listInfinite: (filter: Omit<SystemListOptions, "page">) => [...systemsQueryKeys.all, "list-infinite", filter],
  configurationTrackedFiles: (configId: string) => [...systemsQueryKeys.all, "configurationTrackedFiles", configId],
  configuration: (configId: string) => [...systemsQueryKeys.all, "configuration", configId],
  snapshots: (systemId?: string, configId?: string) => ["snapshots", systemId, configId] as const,
  snapshotItemGroups: (snapshotId?: string, configId?: string, trackedFileIdsKey?: string) => [
    ...systemsQueryKeys.all,
    "snapshotItemGroups",
    snapshotId,
    configId,
    trackedFileIdsKey,
  ],
  snapshot: (snapshotId: string) => [...systemsQueryKeys.all, "snapshot", snapshotId],
  snapshotItems: (snapshotId: string) => [...systemsQueryKeys.all, "snapshotItems", snapshotId],
  snapshotRevisions: (snapshotId: string) => [...systemsQueryKeys.all, "snapshotRevisions", snapshotId],
  allSnapshots: (systemId: string) => [...systemsQueryKeys.all, "allSnapshots", systemId],
  snapshotAvailableAll: () => [...systemsQueryKeys.all, "snapshotAvailable"] as const,
  snapshotAvailable: (configId: string) => [...systemsQueryKeys.snapshotAvailableAll(), configId],
  snapshotsData: (systemId: string, configId: string) => [...systemsQueryKeys.all, "snapshotsData", systemId, configId],
  // Canonical prefix for every per-system snapshot-tag read (version-label lookup, search-results
  // facet) so one prefix invalidation can cover them all, rather than an ad-hoc key per variant.
  snapshotTagsAll: () => [...systemsQueryKeys.all, "snapshotTags"] as const,
  snapshotTags: (systemId: string, variant: SnapshotTagsVariant) =>
    [...systemsQueryKeys.snapshotTagsAll(), systemId, variant] as const,
};

export type SnapshotTagsVariant = { kind: "labelLookup" } | { kind: "searchFacet" };

export type NewTrackedFileResolver = NewTrackedFile & {
  fileRevisionId?: string | null | undefined;
};

export type ResolverValueType = {
  fileId?: string | null | undefined;
  specifierType: SystemFileRevisionSpecifierType;
  fileRevisionId?: string;
};

export type TrackedFileType = {
  created?: Date;
  currentRevisionId?: string | null | undefined;
  revisionId?: string | null | undefined;
  displayFileName?: string | undefined;
  extension?: string | null;
  externalId?: string | null | undefined;
  resourceId?: string | null | undefined;
  fileId?: string | null;
  modelId?: string | null | undefined;
  noAccess?: boolean;
  pinnedRevisionId?: string | null | undefined;
  snapshotId?: string;
  specifierType?: SystemFileRevisionSpecifierType;
  status?: string;
  type?: string | null | undefined;
  revisionIds?: string[];
};

export type DownloadableFile = {
  displayFileName: string;
  extension: string;
  type: string;
  resourceId: string;
  modelId?: string | null;
  revisionId?: string | null;
  pinnedRevisionId?: string | null;
  size?: number;
  folderPath?: string;
};

export type ZipDownloadResult = {
  included: number;
  requested: number;
};

// Minimal snapshot revision type to reduce memory footprint
export type MinimalSnapshotRevision = Pick<
  SnapshotRevisionSearchItem,
  "revisionId" | "displayName" | "name" | "extension" | "resourceId" | "resourceType" | "created" | "isArchived"
>;

export type SystemType = {
  archiveStatus: string;
  created: Date;
  createdById: string;
  description: string;
  id: string;
  items: SnapshotRevisionSearchItem[];
  name: string;
  configurations?: Array<SystemConfiguration>;
  baselineTaggedSnapshotId?: string | null;
  infosecLevel?: InfosecLevel | null;
};

export type SystemConfigurationMenu = SystemConfiguration & {
  value: string;
};

export class SystemInfosecAssignmentError extends Error {
  readonly systemId: string;

  constructor(systemId: string, cause: unknown) {
    super("Failed to assign classification to the created system", { cause });
    this.name = "SystemInfosecAssignmentError";
    this.systemId = systemId;
  }
}

export function isSystemInfosecAssignmentError(error: unknown): error is SystemInfosecAssignmentError {
  return error instanceof SystemInfosecAssignmentError;
}

/**
 * Creates a system without any initial configuration.
 * Used when you want to create an empty system and add configurations later.
 */
export const useCreateSystemWithoutConfig = () =>
  useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      infosecLevelId?: string;
      infosecLevel?: InfosecLevel;
      existingSystemId?: string;
    }) => {
      const system = data.existingSystemId
        ? await resolveExistingSystemForRetry(data.existingSystemId, data.name, data.description)
        : await clientSDK.createSystem({
            name: data.name,
            description: data.description,
          } as NewSystem);

      if (data.infosecLevelId) {
        try {
          const assignLevel = new AssignInfosecLevel(data.infosecLevelId);
          await clientSDK.assignInfosecLevel("system", system.id, assignLevel);
        } catch (cause) {
          throw new SystemInfosecAssignmentError(system.id, cause);
        }
      }

      return { ...system, infosecLevel: data.infosecLevel ?? null };
    },
    onSuccess: (data) => {
      // Set the cache with the system including infosec level to avoid flash
      queryClient.setQueryData(systemsQueryKeys.item(data.id), data);
      queryClient.invalidateQueries({ queryKey: systemsQueryKeys.all });
    },
    onError: () => {
      toast.error("An error occurred while creating the system.");
    },
  });

/**
 * Creates a system with an initial configuration and tracked files.
 * This is the most common way to create a new system with initial content.
 */
export const useCreateSystemWithConfig = () =>
  useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      configName: string;
      trackedFiles: NewTrackedFile[];
      infosecLevelId?: string;
      infosecLevel?: InfosecLevel;
    }) => {
      const system = await clientSDK.createSystem({
        name: data.name,
        description: data.description,
      } as NewSystem);

      if (data.infosecLevelId) {
        const assignLevel = new AssignInfosecLevel(data.infosecLevelId);
        await clientSDK.assignInfosecLevel("system", system.id, assignLevel);
      }

      const configuration = await clientSDK.createConfiguration(system.id, {
        name: data.configName,
        trackedFiles: data.trackedFiles,
      } as NewSystemConfiguration);

      return { ...system, infosecLevel: data.infosecLevel ?? null, configurations: [configuration] };
    },
    onSuccess: (data) => {
      // Set the cache with the system including infosec level to avoid flash
      queryClient.setQueryData(systemsQueryKeys.item(data.id), data);
      queryClient.invalidateQueries({ queryKey: systemsQueryKeys.all });
    },
    onError: () => {
      toast.error("An error occurred while creating the system.");
    },
  });

/**
 * Retrieves a paginated list of systems with enhanced performance through parallel user lookup.
 * Optimizes the user lookup process to reduce overall response time and includes smart caching.
 */
export const useListSystems = ({ filter, enabled = true }: { filter: SystemListOptions; enabled?: boolean }) =>
  useQuery({
    queryKey: systemsQueryKeys.list(filter),
    queryFn: () =>
      clientSDK
        .listSystems(
          filter.page,
          filter.size,
          filter.createdBy,
          filter.archiveStatus ?? "active",
          filter.name ?? null,
          filter.description ?? null,
          filter.recentlyViewed ?? undefined,
          filter.sort,
        )
        .then(async (response) => {
          const list = response.items.map(async (systemItem) => {
            const uploadedByUser = await lookupOrFetchUser(systemItem.createdById!);
            return {
              ...systemItem,
              creatorName: uploadedByUser,
            };
          });
          const result = await Promise.all(list).then((response) => response.flat());
          return {
            items: result,
            total: response.total,
          };
        })
        .catch(() => {
          throw new Error("An error occurred while loading systems.");
        }),
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    enabled,
  });

/**
 * Server-side paginated systems list; fetch further pages with `fetchNextPage()`.
 * Pages are requested with `include_total=false`, so `total` is null and a full
 * page is treated as having a next page. When the listing ends exactly on a
 * page boundary, the last fetch returns an empty final page.
 */
export const useListSystemsInfinite = ({
  filter,
  enabled = true,
}: {
  filter: Omit<SystemListOptions, "page">;
  enabled?: boolean;
}) =>
  useInfiniteQuery({
    queryKey: systemsQueryKeys.listInfinite(filter),
    queryFn: ({ pageParam }) =>
      clientSDK
        .listSystems(
          pageParam,
          filter.size,
          filter.createdBy,
          filter.archiveStatus ?? "active",
          filter.name ?? null,
          filter.description ?? null,
          filter.recentlyViewed ?? undefined,
          filter.sort,
          false, // includeTotal
        )
        .then(async (response: OptionalTotalPageSystem) => {
          const list = response.items.map(async (systemItem) => {
            const creatorName = await lookupOrFetchUser(systemItem.createdById);
            return { ...systemItem, creatorName };
          });
          const items = await Promise.all(list);
          return { items, total: response.total ?? null, page: pageParam };
        })
        .catch(() => {
          throw new Error("An error occurred while loading systems.");
        }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.items.length === filter.size ? lastPageParam + 1 : undefined,
    staleTime: 2 * 60 * 1000,
    enabled,
  });

/**
 * Transforms snapshot revision data to TrackedFileType objects using efficient single-pass processing.
 * Filters out archived items early and sorts results once at the end to minimize iterations and memory usage.
 */
export const processSnapshotRevisions = (
  revisions: SnapshotRevisionSearchItem[],
  options?: {
    filterArchived?: boolean;
    snapshotId?: string;
    sort?: boolean;
  },
): TrackedFileType[] => {
  const { filterArchived = true, snapshotId, sort = true } = options || {};

  const processed = revisions.reduce((acc, revision) => {
    if (filterArchived && revision.isArchived) return acc;

    // Backend may still return legacy Document revisions; the frontend no longer surfaces them.
    if (revision.resourceType === "Document") {
      return acc;
    }

    const processed: TrackedFileType = {
      revisionId: revision.revisionId,
      displayFileName:
        stripExtensionFromDisplayName(revision.displayName, revision.extension) ||
        getFileNameAndExtension(revision.name || "")?.name ||
        "Unknown",
      extension: revision.extension || undefined,
      resourceId: revision.resourceId || undefined,
      type: revision.resourceType || undefined,
      created: revision.created,
      currentRevisionId: revision.revisionId,
      fileId: revision.fileId || undefined,
      modelId: revision.resourceId || undefined,
      pinnedRevisionId: revision.revisionId,
      status: revision.isArchived ? "archived" : "active",
      noAccess: false,
      specifierType: undefined,
      revisionIds: [revision.revisionId],
      externalId: revision.externalIdentifier || undefined,
      ...(snapshotId && { snapshotId }),
    };

    acc.push(processed);
    return acc;
  }, [] as TrackedFileType[]);

  if (sort) {
    return processed.sort((a, b) => (a.displayFileName || "").localeCompare(b.displayFileName || ""));
  }

  return processed;
};

/**
 * Query options for `GET /systems/:id` plus the revisions enrichment.
 * Extracted as a factory so prefetch sites (e.g. card hover) share the exact cache shape
 * `useGetSystem` consumes — otherwise a prefetch with a stub queryFn caches a different
 * shape than the hook expects and breaks the cache hit.
 */
export const systemQueryOptions = (systemId: string | undefined, snapshotId?: string | undefined) =>
  queryOptions({
    queryKey: systemsQueryKeys.item(systemId!, snapshotId),
    queryFn: () =>
      clientSDK
        .getSystem(systemId!)
        .then(async (response) => {
          if (response.configurations?.length === 0) {
            return { ...response, items: [] };
          }

          const configs = response?.configurations?.map((config) => ({
            ...config,
            value: config.id,
            label: config.name,
            setClient: config.setClient,
          })) as SystemConfigurationMenu[];

          const updatedResponse = { ...response, configurations: configs };

          // Use the provided snapshotId, fall back to baseline
          const targetSnapshotId = snapshotId || response.baselineTaggedSnapshotId;

          // If there's no target snapshot ID, return empty items
          if (!targetSnapshotId) {
            return { ...updatedResponse, baselineTagIdSnapshotId: null, items: [] };
          }

          // Use React Query's ensureQueryData for deduplication and caching
          const revisionsData = await queryClient.ensureQueryData(snapshotRevisionsQueryOptions(targetSnapshotId));

          return { ...updatedResponse, baselineTagIdSnapshotId: targetSnapshotId, items: revisionsData.items };
        })
        .catch((error) => {
          if (error.message.includes(String(HttpStatusCodeType.Forbidden))) {
            throw new Error("You don't have access to the system.");
          } else {
            throw new Error("System not found or an error occurred while loading the system.");
          }
        }),
    enabled: !!systemId,
    staleTime: DEFAULT_CACHE_TIME, // Cache for 5 minutes
  });

export const useGetSystem = (systemId: string | undefined, snapshotId?: string | undefined) =>
  useQuery(systemQueryOptions(systemId, snapshotId));

/**
 * Optimized cache invalidation that targets specific snapshot-related queries.
 * Provides more granular cache control to avoid unnecessary refetches while maintaining data consistency.
 */
export const invalidateSnapshotCache = (snapshotId: string) => {
  queryClient.invalidateQueries({
    queryKey: systemsQueryKeys.snapshotRevisions(snapshotId),
    exact: false,
  });

  // Also invalidate related system data
  const systemId = snapshotId.split("-")[0];
  queryClient.invalidateQueries({
    queryKey: systemsQueryKeys.item(systemId),
    exact: false,
  });
};

export function getModelFilesInZip(
  modelItems: DownloadableFile[],
  zipOutputName: string,
  emptyFolderPaths: readonly string[] = [],
): Promise<ZipDownloadResult> {
  return downloadFilesInZip(modelItems, zipOutputName, emptyFolderPaths);
}

export type SnapshotItemForDiff = {
  snapshotId?: string;
  revisionId?: string | null;
  content?: Uint8Array;
  contentSalt?: string;
  contentSha?: string;
  properties?: ModelProperties;
  displayFileName?: string;
  noAccess?: boolean;
};

/**
 * Query options for checking if a snapshot is available for creation using dry-run mode.
 * Validates snapshot creation without actually creating it; freshness is event-driven via
 * mutation invalidation (callers that mutate snapshot inputs must invalidate
 * `systemsQueryKeys.snapshotAvailable`).
 */
export const snapshotAvailableQueryOptions = (configId?: string) =>
  queryOptions({
    queryKey: systemsQueryKeys.snapshotAvailable(configId!),
    // queryFn is a POST that writes an audit-log entry on every call — do not poll.
    queryFn: () => clientSDK.createSnapshot(configId!, { dryRun: true } as NewSnapshot),
    enabled: !!configId,
    retry: false,
    refetchOnMount: "always",
    refetchOnReconnect: "always",
  });

/**
 * Retrieves a specific snapshot by ID with caching and error handling.
 * Includes automatic retry logic and stale-time optimization for better performance.
 */
export const useGetSnapshot = (snapshotId?: string) =>
  useQuery({
    queryKey: systemsQueryKeys.snapshot(snapshotId!),
    queryFn: () =>
      clientSDK
        .getSnapshot(snapshotId!)
        .then((response) => response)
        .catch((error: unknown) => {
          throw new Error("An error occurred while loading the snapshot.", { cause: error });
        }),
    enabled: !!snapshotId,
    // A snapshot is immutable once created, so we can cache it indefinitely
    staleTime: Infinity,
    retry: false,
  });

/**
 * Fetches all snapshots for a system with proper pagination handling.
 * Fetches the first page, then fetches remaining pages in parallel if needed.
 */
const fetchAllSnapshots = async (systemId: string | undefined, configId: string | undefined) => {
  const pageSize = 100;
  const firstResponse = await clientSDK.listSnapshots(systemId, configId, null, 1, pageSize);

  if (!firstResponse.total || firstResponse.total <= pageSize) {
    return firstResponse.items;
  }

  const allItems = [...firstResponse.items];
  const totalPages = firstResponse.pages || Math.ceil(firstResponse.total / pageSize);

  const pagePromises = [];
  for (let page = 2; page <= totalPages; page++) {
    pagePromises.push(clientSDK.listSnapshots(systemId, configId, null, page, pageSize));
  }

  const remainingPages = await Promise.all(pagePromises);
  remainingPages.forEach((pageResponse) => {
    allItems.push(...pageResponse.items);
  });

  return allItems;
};

/**
 * Efficiently loads system snapshots with optimized revision data loading using the new listSnapshotRevisions method.
 * Eliminates N+1 queries by using bulk revision loading and reduces API calls through deduplication.
 */
export const getSystemSnapshots = async (systemId: string | undefined, configId: string | undefined) => {
  try {
    const allSnapshots = await fetchAllSnapshots(systemId, configId);

    const labelledSnapshots = allSnapshots.map((item) => ({
      ...item,
      label: `${item.id.substring(0, item.id.indexOf("-"))} - ${formatDate(item.created)}`,
      value: item.id,
    }));

    return {
      snapshotsMenu: labelledSnapshots,
    };
  } catch {
    throw new Error("An error occurred while loading the system snapshots.");
  }
};

/**
 * Lists snapshots for a system/configuration with smart caching and background refresh capabilities.
 * Includes automatic enabling based on parameter availability and optimized cache timing.
 */
export const useListSnapshots = <TData = Snapshot[]>(
  systemId: string | undefined,
  configId: string | undefined,
  options?: { enabled?: boolean; select?: (data: Snapshot[]) => TData },
) =>
  useQuery({
    queryKey: systemsQueryKeys.snapshots(systemId, configId),
    queryFn: () => fetchAllSnapshots(systemId, configId),
    retry: false,
    enabled: (options?.enabled ?? true) && !!(systemId || configId),
    staleTime: 3 * 60 * 1000, // Cache for 3 minutes
    select: options?.select,
  });

type SnapshotItemsQueryOptions = Omit<
  UseQueryOptions<SnapshotItem[], Error, SnapshotItem[], QueryKey>,
  "queryKey" | "queryFn"
>;

export type GroupedSnapshotItemSuccess = {
  status: "success";
  id: string;
  displayFileName: string;
  resourceId?: string;
  revisionId?: string;
  type?: string;
  archiveStatus?: string;
  isNested?: boolean;
  parentModelRevisionId?: string;
  snapshotId?: string;
  extension?: string;
  created?: Date;
  properties?: ModelProperties;
  contentToken?: {
    sha: string;
    salt: string;
  };
};

export type GroupedSnapshotItemError = {
  status: "error";
  id: string;
  revisionId?: string;
  snapshotId?: string;
  isNested?: boolean;
  parentModelRevisionId?: string;
  error: SnapshotLoadError;
};

export type GroupedSnapshotItem = GroupedSnapshotItemSuccess | GroupedSnapshotItemError;

export type SnapshotItemGroup = {
  parentItem: GroupedSnapshotItem;
  nestedItems: GroupedSnapshotItem[];
  hasNestedItems: boolean;
  nestedRevisionIds?: string[];
  nestedResourceIdToRevisionId?: Record<string, string>;
};

/**
 * Creates a GroupedSnapshotItemSuccess from revision data with standardized properties.
 * Centralizes the logic for creating snapshot items to ensure consistency.
 */
const createGroupedSnapshotItem = (
  snapshotItemId: string,
  revisionData: SnapshotRevisionSearchItem,
  snapshotId: string | undefined,
  isNested: boolean,
  parentModelRevisionId?: string,
): GroupedSnapshotItemSuccess => {
  const extension = revisionData.extension || "";
  const displayFileName =
    stripExtensionFromDisplayName(revisionData.displayName, extension) ||
    getFileNameAndExtension(revisionData.name || "")?.name ||
    "Unknown";

  return {
    status: "success",
    id: snapshotItemId,
    displayFileName,
    resourceId: revisionData.resourceId || undefined,
    revisionId: revisionData.revisionId,
    type: revisionData.resourceType || undefined,
    archiveStatus: revisionData.isArchived ? "archived" : "active",
    snapshotId,
    extension,
    created: revisionData.created,
    isNested,
    parentModelRevisionId,
    properties: {
      displayFileName,
      extension,
    },
    contentToken: revisionData.contentToken
      ? {
          sha: revisionData.contentToken.sha,
          salt: revisionData.contentToken.salt,
        }
      : undefined,
  };
};

/**
 * Creates an error snapshot item for items that couldn't be loaded.
 * Consolidates error item creation logic used throughout snapshot processing.
 */
const createErrorSnapshotItem = (
  item: SnapshotItem,
  error: SnapshotLoadError,
  isNested: boolean = false,
  parentModelRevisionId?: string,
): GroupedSnapshotItemError => ({
  status: "error",
  id: item.id,
  revisionId: item.fileRevisionId,
  snapshotId: item.snapshotId,
  isNested,
  parentModelRevisionId,
  error,
});

/**
 * Creates an error SnapshotItemGroup for items that couldn't be loaded.
 * Consolidates error group creation logic used throughout snapshot processing.
 */
const createErrorGroup = (item: SnapshotItem, error: SnapshotLoadError): SnapshotItemGroup => ({
  parentItem: createErrorSnapshotItem(item, error, false),
  nestedItems: [],
  hasNestedItems: false,
  nestedRevisionIds: [],
  nestedResourceIdToRevisionId: {},
});

/**
 * Paginates an array and returns the paginated items along with metadata.
 * Calculates total count, hasMore flag, and slices the array based on offset and limit.
 */
const paginateArray = <T>(
  items: T[],
  offset: number,
  limit: number,
): { items: T[]; totalCount: number; hasMore: boolean } => {
  const totalCount = items.length;
  const hasMore = offset + limit < totalCount;
  const paginatedItems = items.slice(offset, offset + limit);

  return {
    items: paginatedItems,
    totalCount,
    hasMore,
  };
};

/**
 * Loads initial snapshot item groups directly from snapshot revisions and tracked files.
 * No longer requires snapshot-items endpoint - uses revisions filtered by tracked file IDs.
 *
 * @param snapshotRevisions - All revision data from the snapshot revisions API
 * @param trackedFiles - Array of tracked files from configuration. Only revisions with
 *   matching fileId will be shown at the top level. For inaccessible files, uses
 *   currentFileRevisionId to enable proper display lookups.
 * @param snapshotId - The snapshot ID for reference
 */
export const loadInitialSnapshotItemGroups = (
  snapshotRevisions: SnapshotRevisionSearchItem[],
  trackedFiles: ConfigurationTrackedFile[],
  snapshotId?: string,
): SnapshotItemGroup[] => {
  const allowedResourceTypes = new Set([SystemResourceType.Model, SystemResourceType.Artifact]);

  const trackedFileIds = new Set(trackedFiles.map((f) => f.fileId));
  const trackedFileMap = new Map(trackedFiles.map((f) => [f.fileId, f]));
  const revisionFileIds = new Set(snapshotRevisions.filter((rev) => rev.fileId).map((rev) => rev.fileId as string));

  const topLevelRevisions = snapshotRevisions.filter((rev) => {
    if (!allowedResourceTypes.has(rev.resourceType as SystemResourceType)) {
      return false;
    }
    return rev.fileId ? trackedFileIds.has(rev.fileId) : false;
  });

  const groups: SnapshotItemGroup[] = topLevelRevisions.map((revisionData) => {
    const groupedFile = createGroupedSnapshotItem(revisionData.revisionId, revisionData, snapshotId, false, undefined);
    const isModel = revisionData.resourceType === SystemResourceType.Model;

    return {
      parentItem: groupedFile,
      nestedItems: [],
      hasNestedItems: isModel && Boolean(revisionData.hasArtifacts),
      nestedRevisionIds: undefined,
      nestedResourceIdToRevisionId: undefined,
    };
  });

  const noAccessFileIds = Array.from(trackedFileIds).filter((fileId) => !revisionFileIds.has(fileId));
  const noAccessGroups: SnapshotItemGroup[] = noAccessFileIds.map((fileId) => {
    const trackedFile = trackedFileMap.get(fileId);
    const revisionId = trackedFile?.currentFileRevisionId || fileId;
    return createErrorGroup({ id: fileId, fileRevisionId: revisionId, snapshotId: snapshotId ?? "" } as SnapshotItem, {
      type: "no_access",
      revisionId,
    });
  });

  const allGroups = [...groups, ...noAccessGroups];

  return allGroups.sort((a, b) => {
    const aName = a.parentItem.status === "success" ? a.parentItem.displayFileName : a.parentItem.revisionId || "";
    const bName = b.parentItem.status === "success" ? b.parentItem.displayFileName : b.parentItem.revisionId || "";
    return aName.localeCompare(bName);
  });
};

/**
 * Loads nested snapshot items with pagination support for efficient memory usage.
 * Only loads visible nested items on-demand to optimize performance for large datasets with many nested artifacts.
 */
export const loadNestedSnapshotItems = async (
  parentRevisionId: string,
  nestedRevisionIds: string[],
  snapshotRevisions: SnapshotRevisionSearchItem[],
  snapshotId: string | undefined,
  offset: number = 0,
  limit: number = 10,
): Promise<{
  nestedSnapshotItems: GroupedSnapshotItem[];
  totalCount: number;
  hasMore: boolean;
  // eslint-disable-next-line max-params -- TODO fix later
}> => {
  if (!nestedRevisionIds || nestedRevisionIds.length === 0) {
    return {
      nestedSnapshotItems: [],
      totalCount: 0,
      hasMore: false,
    };
  }

  const snapshotRevisionMap = new Map(snapshotRevisions.map((rev) => [rev.revisionId, rev]));

  const validRevisionIds = nestedRevisionIds.filter((revisionId) => snapshotRevisionMap.has(revisionId));

  const { items: pageItems, totalCount, hasMore } = paginateArray(validRevisionIds, offset, limit);

  const nestedSnapshotItems: GroupedSnapshotItem[] = pageItems.map((revisionId) => {
    const revisionData = snapshotRevisionMap.get(revisionId);

    if (!revisionData) {
      return {
        status: "error",
        id: revisionId,
        revisionId: revisionId,
        snapshotId: snapshotId,
        isNested: true,
        parentModelRevisionId: parentRevisionId,
        error: { type: "not_found", revisionId },
      } as GroupedSnapshotItemError;
    }

    return createGroupedSnapshotItem(revisionId, revisionData, snapshotId, true, parentRevisionId);
  });

  nestedSnapshotItems.sort((a, b) => {
    const aIsError = a.status === "error";
    const bIsError = b.status === "error";
    if (aIsError !== bIsError) return aIsError ? 1 : -1;

    const aName = a.status === "success" ? a.displayFileName : a.revisionId || "";
    const bName = b.status === "success" ? b.displayFileName : b.revisionId || "";
    return aName.localeCompare(bName);
  });

  return {
    nestedSnapshotItems,
    totalCount,
    hasMore,
  };
};

/**
 * Efficiently loads all snapshot items with parallel pagination for large datasets.
 * Automatically detects total count and fetches remaining pages in parallel to minimize total request time.
 */
export const useListAllSnapshotItems = (
  snapshotId: string | undefined,
  options?: SnapshotItemsQueryOptions,
): UseQueryResult<SnapshotItem[], Error> =>
  useQuery<SnapshotItem[], Error>({
    queryKey: systemsQueryKeys.snapshotItems(snapshotId!),
    queryFn: async () => {
      if (!snapshotId) return [];
      const firstResponse = await clientSDK.listSnapshotItems(snapshotId, 1, 100);
      if (!firstResponse.total || firstResponse.total <= 100) {
        return firstResponse.items;
      }
      const allItems: SnapshotItem[] = [...firstResponse.items];
      const totalPages = firstResponse.pages || Math.ceil(firstResponse.total / 100);

      const pagePromises = [];
      for (let page = 2; page <= totalPages; page++) {
        pagePromises.push(clientSDK.listSnapshotItems(snapshotId, page, 100));
      }

      const remainingPages = await Promise.all(pagePromises);
      remainingPages.forEach((pageResponse) => {
        allItems.push(...pageResponse.items);
      });

      return allItems;
    },
    enabled: !!snapshotId,
    retry: false,
    staleTime: 3 * 60 * 1000, // Cache for 3 minutes
    ...options,
  });

export { useListConfigurationTrackedFiles } from "./configurations";
export type { ConfigurationTrackedFile } from "./configurations";

export type SnapshotItemWithTag = PageSnapshotItem & {
  configurationId: string;
  id: string;
  snapshotId: string;
  snapshotTags: SnapshotTag[];
};

/**
 * Retrieves system access information with conditional enabling for permission management.
 * Used for sharing functionality and access control with optimized cache timing.
 */
export const useGetSystemAccessById = (systemId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: systemAccessQueryKey(systemId),
    queryFn: () =>
      clientSDK
        .listAccess(ResourceType.System, systemId!)
        .then((response) => response)
        .catch(() => {
          throw new Error("An error occurred while loading the system access.");
        }),
    ...{ enabled: enabled && !!systemId },
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });

/**
 * Updates a system's name and description with optimistic updates and rollback capability.
 * Provides immediate UI feedback and maintains cache consistency across all related queries.
 */
export const useEditSystem = () =>
  useMutation({
    mutationFn: async (data: {
      name: string | undefined;
      description: string | undefined;
      systemId: string | undefined;
      infosecLevelId?: string;
      infosecLevel?: InfosecLevel;
    }) => {
      const updatedSystem = await clientSDK.updateSystem(data.systemId!, {
        name: data.name!,
        description: data.description!,
      } as UpdateSystem);

      if (data.infosecLevelId) {
        try {
          const assignLevel = new AssignInfosecLevel(data.infosecLevelId);
          await clientSDK.assignInfosecLevel("system", data.systemId!, assignLevel);
        } catch (cause) {
          throw new SystemInfosecAssignmentError(data.systemId!, cause);
        }
      }

      return { ...updatedSystem, infosecLevel: data.infosecLevel ?? null };
    },
    onMutate: async (variables) => {
      const { systemId, name, description, infosecLevel } = variables;

      // Cancel any active queries for the system to prevent overwrite of the optimistic update
      await queryClient.cancelQueries({ queryKey: systemsQueryKeys.all });

      // Snapshot current data for rollback if needed
      const previousItemData = queryClient.getQueryData(systemsQueryKeys.item(systemId!));
      const previousListData = queryClient.getQueriesData({ queryKey: systemsQueryKeys.all });

      // Apply optimistic update to item cache
      queryClient.setQueryData(systemsQueryKeys.item(systemId!), (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          name,
          description,
          ...(infosecLevel ? { infosecLevel } : {}),
        };
      });

      queryClient.setQueriesData({ queryKey: systemsQueryKeys.all }, (oldData) => {
        if (!oldData || typeof oldData !== "object" || !("items" in oldData)) return oldData;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const listData = oldData as { items: any[]; total: number };
        const updatedItems = listData.items.map((item) =>
          item.id === systemId ? { ...item, name, description, ...(infosecLevel ? { infosecLevel } : {}) } : item,
        );

        return {
          ...listData,
          items: updatedItems,
        };
      });

      // Provide snapshots for rollback
      return { previousItemData, previousListData };
    },
    // Runs if mutation fails; rollback to previous cached values. A classification-assignment
    // failure means updateSystem already persisted name/description, so rolling those back would
    // show the user stale values the backend no longer holds — keep the optimistic update instead.
    onError: (error, _variables, context) => {
      if (isSystemInfosecAssignmentError(error)) {
        toast.error("System details saved, but the classification could not be updated.");
        return;
      }

      if (context?.previousItemData) {
        queryClient.setQueryData(systemsQueryKeys.item(_variables.systemId!), context.previousItemData);
      }
      if (context?.previousListData) {
        context.previousListData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error("An error occurred while editing the system.");
    },
    // Runs if mutation succeeds
    onSuccess: () => {
      toast.success("System updated successfully.");
    },
    // Runs after success or failure; triggers cache revalidation from backend
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: systemsQueryKeys.item(variables.systemId!) });
      queryClient.invalidateQueries({ queryKey: systemsQueryKeys.all });
    },
  });

/**
 * Manages system sharing permissions with bulk operations support.
 * Handles user access grants, updates, and removals efficiently with proper cache invalidation.
 */
export const useShareSystem = () =>
  useMutation({
    mutationFn: async (data: {
      systemId: string;
      accessGrants: {
        action: AccessActionType;
        userId: string | undefined;
        accessRelationship: SystemAccessRelationshipPayload;
      }[];
    }) => {
      const createGrants = data.accessGrants.filter((item) => item.action === AccessActionType.create);
      const updateGrants = data.accessGrants.filter((item) => item.action === AccessActionType.update);
      const removeGrants = data.accessGrants.filter((item) => item.action === AccessActionType.remove);

      const promises: Promise<unknown>[] = [];

      // Handle bulk create operations
      if (createGrants.length > 0) {
        // Separate org users from cross-tenant emails
        const orgUserGrants = createGrants.filter((item) => item.userId);
        const crossTenantEmailGrants = createGrants.filter((item) => !item.userId);

        // Bulk create for org users
        if (orgUserGrants.length > 0) {
          const accessRelationships = orgUserGrants.map((item) => item.accessRelationship as AccessRelationship);
          promises.push(clientSDK.bulkCreateAccess(accessRelationships));
        }

        // Individual calls for cross-tenant emails (no bulk endpoint available)
        crossTenantEmailGrants.forEach((item) => {
          promises.push(
            clientSDK.createAccessByEmailForOtherTenants(
              item.accessRelationship.subjectType,
              item.accessRelationship.subjectId,
              ResourceType.System,
              item.accessRelationship.resourceId,
              item.accessRelationship.relation,
            ),
          );
        });
      }

      // Handle update operations individually
      updateGrants.forEach((item) => {
        promises.push(
          clientSDK.updateAccess(
            "user",
            item.accessRelationship.subjectId,
            ResourceType.System,
            data.systemId,
            item.accessRelationship as AccessRelationship,
          ),
        );
      });

      // Handle remove operations individually
      removeGrants.forEach((item) => {
        promises.push(
          clientSDK.removeAccess("user", item.accessRelationship.subjectId, ResourceType.System, data.systemId),
        );
      });

      await Promise.all(promises);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: systemAccessQueryKey(variables.systemId) });
    },
    meta: {
      skipGlobalInvalidation: true,
    },
  });

export type ParentSnapshotItem = {
  id: string;
  resourceId: string;
  displayFileName: string;
  type: "model" | "artifact";
  extension?: string;
};

/**
 * Filters snapshot file groups to extract only parent items for sharing.
 * Nested items do not need to be individually shared as they inherit permissions from parent models.
 */
export const filterParentSnapshotItems = (
  snapshotFileGroups: {
    parent: {
      _tag: string;
      id: string;
      resourceId?: string;
      displayFileName?: string;
      type?: string;
      extension?: string;
    };
  }[],
): ParentSnapshotItem[] => {
  return snapshotFileGroups
    .filter((group) => group.parent._tag === "accessible" && group.parent.resourceId)
    .map((group) => {
      const parent = group.parent;
      if (parent._tag !== "accessible") {
        throw new Error("Unexpected inaccessible item after filtering");
      }
      return {
        id: parent.id,
        resourceId: parent.resourceId!,
        displayFileName: parent.displayFileName || "",
        type: parent.type === SystemResourceType.Artifact ? "artifact" : "model",
        extension: parent.extension,
      };
    });
};

/**
 * Hook to get shareable snapshot files by checking user edit permissions.
 * Only returns files that the current user has permission to share with others.
 */
export const useGetShareableSnapshotFiles = (parentSnapshotItems: ParentSnapshotItem[]) =>
  useQuery({
    queryKey: ["shareableSnapshotFiles", parentSnapshotItems.map((item) => item.id).join(",")],
    queryFn: async (): Promise<ParentSnapshotItem[]> => {
      if (!parentSnapshotItems.length) {
        return [];
      }

      // Get snapshot files that the user can edit
      const resourceIds = parentSnapshotItems.map((item) => item.resourceId);
      const shareableResources = await clientSDK.listResources(
        1,
        100,
        resourceIds,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        "editor",
      );

      const shareableResourceIds = new Set(shareableResources.items.map((resource: ResourceSearchItem) => resource.id));
      return parentSnapshotItems.filter((item) => shareableResourceIds.has(item.resourceId));
    },
    enabled: Boolean(parentSnapshotItems.length),
    staleTime: DEFAULT_CACHE_TIME, // Cache for 5 minutes
  });

/**
 * Bulk shares multiple snapshot files with multiple users efficiently.
 * Creates access relationships in batch to minimize API calls and improve performance.
 */
export const useBulkShareSnapshotFiles = () =>
  useMutation({
    mutationFn: async (data: {
      permission: UserGrantType.viewer | UserGrantType.editor;
      userIds: string[];
      shareableFiles: ParentSnapshotItem[];
    }) => {
      if (!data.shareableFiles.length) {
        throw new Error("No files available to share");
      }

      const accessRelationships: AccessRelationship[] = [];

      for (const userId of data.userIds) {
        for (const file of data.shareableFiles) {
          const resourceType = file.type === "artifact" ? AccessResourceType.Artifact : AccessResourceType.Model;

          const accessRelationship = {
            subjectType: "user",
            subjectId: userId,
            resourceType,
            resourceId: file.resourceId,
            relation: data.permission,
          } as AccessRelationship;

          accessRelationships.push(accessRelationship);
        }
      }

      await clientSDK.bulkCreateAccess(accessRelationships);
      return {
        filesShared: data.shareableFiles.length,
        usersGranted: data.userIds.length,
      };
    },
  });

/**
 * Creates matched and unmatched pairs from top-level snapshot file groups for diff comparison.
 * Only processes parent items, ignoring nested items. Content is NOT loaded here —
 * it is lazy-loaded per file when the user expands the accordion.
 */
export function createTopLevelDiffPairs(
  leftSnapshotFileGroups: {
    parent: {
      _tag: string;
      snapshotId?: string;
      revisionId: string;
      displayFileName?: string;
      properties?: ModelProperties;
      contentToken?: { sha: string; salt: string };
    };
  }[],
  rightSnapshotFileGroups: {
    parent: {
      _tag: string;
      snapshotId?: string;
      revisionId: string;
      displayFileName?: string;
      properties?: ModelProperties;
      contentToken?: { sha: string; salt: string };
    };
  }[],
): {
  matchedPairs: { left: SnapshotItemForDiff; right: SnapshotItemForDiff }[];
  unmatchedLeft: SnapshotItemForDiff[];
  unmatchedRight: SnapshotItemForDiff[];
} {
  const rightMap = new Map<string, SnapshotItemForDiff>();
  rightSnapshotFileGroups.forEach((group) => {
    const rightItem = group.parent;

    if (rightItem._tag === "inaccessible") {
      rightMap.set(rightItem.revisionId || "", {
        snapshotId: rightItem.snapshotId,
        revisionId: rightItem.revisionId,
        noAccess: true,
      });
      return;
    }

    rightMap.set(rightItem.contentToken?.salt || rightItem.revisionId || "", {
      snapshotId: rightItem.snapshotId,
      revisionId: rightItem.revisionId,
      displayFileName: rightItem.displayFileName,
      noAccess: false,
      properties: rightItem.properties,
      contentSalt: rightItem.contentToken?.salt,
      contentSha: rightItem.contentToken?.sha,
    });
  });

  const leftMap = new Map<string, SnapshotItemForDiff>();
  leftSnapshotFileGroups.forEach((group) => {
    const leftItem = group.parent;

    if (leftItem._tag === "inaccessible") {
      leftMap.set(leftItem.revisionId || "", {
        snapshotId: leftItem.snapshotId,
        revisionId: leftItem.revisionId,
        noAccess: true,
      });
      return;
    }

    leftMap.set(leftItem.contentToken?.salt || leftItem.revisionId || "", {
      snapshotId: leftItem.snapshotId,
      revisionId: leftItem.revisionId,
      displayFileName: leftItem.displayFileName,
      noAccess: false,
      properties: leftItem.properties,
      contentSalt: leftItem.contentToken?.salt,
      contentSha: leftItem.contentToken?.sha,
    });
  });

  const matchedPairs: { left: SnapshotItemForDiff; right: SnapshotItemForDiff }[] = [];
  const unmatchedLeft: SnapshotItemForDiff[] = [];
  const unmatchedRight: SnapshotItemForDiff[] = [];

  leftSnapshotFileGroups.forEach((leftGroup) => {
    const leftItem = leftGroup.parent;
    const key =
      leftItem._tag === "inaccessible"
        ? leftItem.revisionId || ""
        : leftItem.contentToken?.salt || leftItem.revisionId || "";
    const leftItemForDiff = leftMap.get(key);
    if (!leftItemForDiff) return;

    const matchKey =
      leftItem._tag === "inaccessible"
        ? leftItem.revisionId || ""
        : leftItem.contentToken?.salt || leftItem.revisionId || "";
    const match = matchKey ? rightMap.get(matchKey) : undefined;
    if (match) {
      matchedPairs.push({ left: leftItemForDiff, right: match });
    } else {
      unmatchedLeft.push(leftItemForDiff);
    }
  });

  rightSnapshotFileGroups.forEach((rightGroup) => {
    const rightItem = rightGroup.parent;
    const key =
      rightItem._tag === "inaccessible"
        ? rightItem.revisionId || ""
        : rightItem.contentToken?.salt || rightItem.revisionId || "";
    if (key && !leftMap.has(key)) {
      const rightItemForDiff = rightMap.get(key);
      if (rightItemForDiff) {
        unmatchedRight.push(rightItemForDiff);
      }
    }
  });

  return {
    matchedPairs,
    unmatchedLeft,
    unmatchedRight,
  };
}

async function resolveExistingSystemForRetry(systemId: string, name: string, description: string) {
  const existing = await clientSDK.getSystem(systemId);
  const persistedName = existing.name ?? "";
  const persistedDescription = existing.description ?? "";

  if (name === persistedName && description === persistedDescription) {
    return existing;
  }

  return clientSDK.updateSystem(systemId, { name, description } as UpdateSystem);
}
